<section class="ai-actions pt-2">
  <p class="text-overline text-neutral-60 m-0 mb-1.5">
    {{ 'iqboost.suggestedActions.title' | translate }}
  </p>

  <!-- Always there, so a notice put in it is announced. -->
  <output class="block">
    @if (unread()) {
      <span
        class="ai-actions-unread flex flex-wrap items-center gap-2 mb-2 text-caption text-neutral-70"
      >
        {{ 'iqboost.suggestedActions.notChecked' | translate }}
        <button
          kendoButton
          type="button"
          class="ai-actions-recheck"
          fillMode="outline"
          size="small"
          (click)="recheck()"
        >
          {{ 'iqboost.suggestedActions.retry' | translate }}
        </button>
      </span>
    }
  </output>

  <div
    class="grid gap-2"
    [class.ai-cards-2]="cards().length === 2"
    [class.ai-cards-3]="cards().length === 3"
  >
    @for (card of cards(); track card.id.index) {
      @let status = statusOf(card);
      @let date = dateOf(card, status);
      <article
        class="ai-action flex flex-col gap-1.5 min-w-0 rounded border border-solid border-neutral-20 bg-white p-3"
        [attr.data-index]="card.id.index"
      >
        <div class="flex items-center gap-1.5 text-caption text-neutral-70">
          <span class="icon-12 shrink-0" [class]="card.icon" aria-hidden="true">
          </span>
          <span class="font-medium text-neutral-80">
            {{ card.typeKey | translate }}
          </span>
          <span aria-hidden="true">·</span>
          <span class="ai-action-date" [class.text-warning-140]="date.past">
            {{ date.text }}
            @if (date.past) {
              <span class="sr-only">
                {{ 'iqboost.suggestedActions.past' | translate }}
              </span>
            }
          </span>
        </div>

        <h4 class="subtitle-2 text-black m-0 break-words">
          {{ card.action.title }}
        </h4>

        @if (card.action.reason; as reason) {
          <p class="ai-action-reason text-caption text-neutral-70 m-0">
            {{ reason }}
          </p>
        }

        @if (card.action.description; as description) {
          <p class="ai-action-description text-body-2 text-neutral-90 m-0">
            {{ description }}
          </p>
        }

        @if (nameOf(card); as name) {
          <p class="ai-action-for text-caption text-neutral-70 m-0">
            {{ 'iqboost.suggestedActions.for' | translate: { name } }}
          </p>
        }

        <!-- Focus moves here when the card's button goes, and what follows is announced. -->
        <div
          class="ai-action-status mt-auto pt-1 flex items-center gap-2 min-h-8 outline-none"
          tabindex="-1"
          aria-live="polite"
        >
          @switch (status.state) {
            @case ('working') {
              <kendo-loader type="infinite-spinner" size="small"></kendo-loader>
              <span class="text-caption text-neutral-70">
                {{ 'iqboost.suggestedActions.creating' | translate }}
              </span>
            }
            @case ('created') {
              <span
                class="inline-flex items-center gap-1 text-caption font-medium text-bush-green-140"
              >
                <span class="icon-10" [class]="createdIcon" aria-hidden="true">
                </span>
                {{ 'iqboost.suggestedActions.created' | translate }}
              </span>
              <button
                type="button"
                class="ai-action-open inline-flex items-center gap-1 p-0 border-0 bg-transparent text-caption font-medium text-finserv-100 cursor-pointer hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-finserv-100 rounded"
                (click)="open(card)"
              >
                {{ 'iqboost.suggestedActions.open' | translate }}
                <span class="icon-10" [class]="openIcon" aria-hidden="true">
                </span>
              </button>
            }
            @case ('failed') {
              <span class="text-caption text-error-100">
                {{ 'iqboost.suggestedActions.failed' | translate }}
              </span>
              <button
                kendoButton
                type="button"
                class="ai-action-create"
                fillMode="outline"
                size="small"
                [disabled]="restoring() || unread()"
                (click)="retry(card)"
              >
                {{ 'iqboost.suggestedActions.retry' | translate }}
              </button>
            }
            @default {
              <button
                kendoButton
                type="button"
                class="ai-action-create"
                fillMode="outline"
                size="small"
                [disabled]="restoring() || unread()"
                (click)="confirm(card)"
              >
                {{ card.createKey | translate }}
              </button>
            }
          }
        </div>
      </article>
    }
  </div>
</section>

@if (confirming(); as card) {
  <ai-suggested-action-dialog
    [action]="card.action"
    [when]="card.when"
    [past]="card.past"
    [name]="nameOf(card) ?? null"
    (confirmed)="create(card)"
    (cancelled)="cancel(card)"
  ></ai-suggested-action-dialog>
}
